import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import {ChevronUp, ChevronDown, Trash2, Plus, Save, X, Edit3, Eye, EyeOff, RefreshCw} from 'lucide-react'
import RichTextEditor from '@/components/RichTextEditor'
import { useFaqs, FaqCategory, FaqItem } from '@/hooks/useFaqs'
import { useAuth } from '@/hooks/useAuth'

const CATEGORIES: FaqCategory[] = ['Candidats', 'Employeurs', 'Formation', 'BNSSA', 'MNS']

export default function FaqEditor() {
  const { user } = useAuth()
  const { items, loading, error, fetchAll, createFaq, updateFaq, deleteFaq, moveFaq } = useFaqs()
  const [activeCategory, setActiveCategory] = useState<FaqCategory>('Candidats')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draftQuestion, setDraftQuestion] = useState('')
  const [draftAnswer, setDraftAnswer] = useState('')
  const [draftActive, setDraftActive] = useState(true)
  const [creating, setCreating] = useState(false)
  const [saving, setSaving] = useState(false)

  const filteredItems = useMemo(
    () =>
      items
        .filter((i) => i.category === activeCategory)
        .sort((a, b) => a.order - b.order),
    [items, activeCategory],
  )

  const counts = useMemo(() => {
    const map: Record<FaqCategory, number> = {
      Candidats: 0,
      Employeurs: 0,
      Formation: 0,
      BNSSA: 0,
      MNS: 0,
    }
    items.forEach((i) => {
      map[i.category] = (map[i.category] || 0) + 1
    })
    return map
  }, [items])

  const beginEdit = (item: FaqItem) => {
    setEditingId(item._id)
    setDraftQuestion(item.question)
    setDraftAnswer(item.answer)
    setDraftActive(item.isActive ?? true)
    setCreating(false)
  }

  const beginCreate = () => {
    setEditingId(null)
    setDraftQuestion('')
    setDraftAnswer('<p></p>')
    setDraftActive(true)
    setCreating(true)
  }

  const cancelEdit = () => {
    setEditingId(null)
    setCreating(false)
    setDraftQuestion('')
    setDraftAnswer('')
  }

  const handleSave = async () => {
    if (!draftQuestion.trim()) {
      toast.error('La question est requise.')
      return
    }
    if (!draftAnswer.trim() || draftAnswer === '<p></p>' || draftAnswer === '<br>') {
      toast.error('La réponse est requise.')
      return
    }
    setSaving(true)
    try {
      if (creating) {
        const maxOrder = filteredItems.reduce((m, i) => Math.max(m, i.order), 0)
        await createFaq(
          {
            question: draftQuestion.trim(),
            answer: draftAnswer,
            category: activeCategory,
            order: maxOrder + 1,
            isActive: draftActive,
          },
          user?.userId,
        )
        toast.success('Question créée.')
      } else if (editingId) {
        await updateFaq(editingId, {
          question: draftQuestion.trim(),
          answer: draftAnswer,
          isActive: draftActive,
        })
        toast.success('Question mise à jour.')
      }
      cancelEdit()
    } catch (e) {
      console.error('[FaqEditor] save failed:', e)
      toast.error('Échec de l\'enregistrement.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Supprimer cette question ? Cette action est définitive.')) return
    try {
      await deleteFaq(id)
      toast.success('Question supprimée.')
      if (editingId === id) cancelEdit()
    } catch (e) {
      console.error('[FaqEditor] delete failed:', e)
      toast.error('Échec de la suppression.')
    }
  }

  const handleMove = async (id: string, direction: 'up' | 'down') => {
    try {
      await moveFaq(id, direction)
    } catch (e) {
      console.error('[FaqEditor] move failed:', e)
      toast.error('Échec du déplacement.')
    }
  }

  const handleToggleActive = async (item: FaqItem) => {
    try {
      await updateFaq(item._id, { isActive: !(item.isActive ?? true) })
      toast.success((item.isActive ?? true) ? 'Question masquée.' : 'Question publiée.')
    } catch (e) {
      console.error('[FaqEditor] toggle failed:', e)
      toast.error('Échec de la mise à jour.')
    }
  }

  // Reset éditeur quand on change d'onglet
  useEffect(() => {
    cancelEdit()
  }, [activeCategory])

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
      <div className="px-5 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-bold text-gray-900">Édition FAQ</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Gérez les questions publiées sur <code className="bg-white px-1 rounded">/faq</code> par catégorie.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchAll}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Rafraîchir
          </button>
          <button
            onClick={beginCreate}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-gray-900 rounded-lg hover:bg-black"
          >
            <Plus className="w-3.5 h-3.5" />
            Nouvelle question
          </button>
        </div>
      </div>

      {/* Onglets catégories */}
      <div className="px-5 py-3 border-b border-gray-200 bg-white flex flex-wrap gap-2">
        {CATEGORIES.map((c) => {
          const active = c === activeCategory
          return (
            <button
              key={c}
              onClick={() => setActiveCategory(c)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                active
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {c}
              <span
                className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  active ? 'bg-white text-gray-900' : 'bg-white text-gray-600 border border-gray-200'
                }`}
              >
                {counts[c] || 0}
              </span>
            </button>
          )
        })}
      </div>

      <div className="p-5">
        {error && (
          <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
            {error}
          </div>
        )}

        {/* Editeur (création/édition) */}
        {(creating || editingId) && (
          <div className="mb-5 border-2 border-gray-900 rounded-xl p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-gray-900">
                {creating ? `Nouvelle question — ${activeCategory}` : 'Modifier la question'}
              </h3>
              <button
                onClick={cancelEdit}
                className="text-gray-400 hover:text-gray-700"
                title="Annuler"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Question</label>
                <input
                  type="text"
                  value={draftQuestion}
                  onChange={(e) => setDraftQuestion(e.target.value)}
                  placeholder="Quelle est la différence entre…"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Réponse (texte enrichi)</label>
                <RichTextEditor
                  value={draftAnswer}
                  onChange={setDraftAnswer}
                  placeholder="Rédigez la réponse… utilisez les boutons pour mettre en forme."
                  minHeight={180}
                />
              </div>

              <label className="inline-flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={draftActive}
                  onChange={(e) => setDraftActive(e.target.checked)}
                  className="rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                />
                Question publiée (visible sur /faq)
              </label>

              <div className="flex items-center gap-2 pt-2">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                >
                  <Save className="w-3.5 h-3.5" />
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
                <button
                  onClick={cancelEdit}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Liste */}
        {loading && filteredItems.length === 0 && (
          <div className="text-center py-10 text-sm text-gray-500">
            <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
            Chargement…
          </div>
        )}

        {!loading && filteredItems.length === 0 && (
          <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-xl">
            <p className="text-sm text-gray-500 mb-3">
              Aucune question dans <strong>{activeCategory}</strong>.
            </p>
            <button
              onClick={beginCreate}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-gray-900 rounded-lg hover:bg-black"
            >
              <Plus className="w-3.5 h-3.5" />
              Créer la première question
            </button>
          </div>
        )}

        <ul className="space-y-2">
          {filteredItems.map((item, idx) => {
            const isEditingThis = editingId === item._id
            const isHidden = item.isActive === false
            return (
              <li
                key={item._id}
                className={`border rounded-xl p-4 transition-colors ${
                  isEditingThis
                    ? 'border-gray-900 bg-gray-50'
                    : isHidden
                    ? 'border-gray-200 bg-gray-50/50 opacity-60'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                        #{idx + 1}
                      </span>
                      {isHidden && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                          Masquée
                        </span>
                      )}
                    </div>
                    <h4 className="text-sm font-bold text-gray-900 leading-snug mb-1">
                      {item.question}
                    </h4>
                    <div
                      className="text-xs text-gray-600 leading-relaxed line-clamp-2 [&_p]:inline [&_*]:inline"
                      dangerouslySetInnerHTML={{ __html: item.answer }}
                    />
                  </div>

                  <div className="flex flex-col items-end gap-1">
                    <div className="flex items-center gap-1">
                      <IconBtn
                        title="Monter"
                        onClick={() => handleMove(item._id, 'up')}
                        disabled={idx === 0}
                      >
                        <ChevronUp className="w-3.5 h-3.5" />
                      </IconBtn>
                      <IconBtn
                        title="Descendre"
                        onClick={() => handleMove(item._id, 'down')}
                        disabled={idx === filteredItems.length - 1}
                      >
                        <ChevronDown className="w-3.5 h-3.5" />
                      </IconBtn>
                    </div>
                    <div className="flex items-center gap-1">
                      <IconBtn
                        title={isHidden ? 'Publier' : 'Masquer'}
                        onClick={() => handleToggleActive(item)}
                      >
                        {isHidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </IconBtn>
                      <IconBtn title="Modifier" onClick={() => beginEdit(item)}>
                        <Edit3 className="w-3.5 h-3.5" />
                      </IconBtn>
                      <IconBtn
                        title="Supprimer"
                        onClick={() => handleDelete(item._id)}
                        variant="danger"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </IconBtn>
                    </div>
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}

function IconBtn({
  children,
  onClick,
  title,
  disabled,
  variant,
}: {
  children: React.ReactNode
  onClick: () => void
  title: string
  disabled?: boolean
  variant?: 'danger'
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`inline-flex items-center justify-center w-7 h-7 rounded-md border transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
        variant === 'danger'
          ? 'border-red-200 text-red-600 bg-white hover:bg-red-50'
          : 'border-gray-200 text-gray-700 bg-white hover:bg-gray-100'
      }`}
    >
      {children}
    </button>
  )
}
